<template>
  <v-dialog v-model="dialog" max-width="700px">
    <v-card>
      <v-card-title>
        {{ isEditMode ? 'Edit Trade Stock' : 'Add Trade Stock' }}
      </v-card-title>

      <v-card-text>
        <v-form @submit.prevent="submitForm">
          <v-row dense>
            <!-- Platform Selection -->
            <v-col cols="12">
              <v-select
                v-model="selectedPlatform"
                :items="platformOptions.length ? platformOptions : []"
                item-title="label"
                item-value="value"
                label="Select Platform"
                variant="outlined"
                required
                @update:modelValue="onPlatformChange"
              />
            </v-col>

            <!-- Stock Selection -->
            <v-col cols="12">
              <v-select
                v-model="formData.stock_id"
                :items="computedStockOptions"
                item-title="label"
                item-value="value"
                label="Select Stock"
                variant="outlined"
                required
                @update:modelValue="onStockChange"
                :disabled="!selectedPlatform"
              />
            </v-col>

            <!-- Code -->
            <v-col cols="12" md="6">
              <v-text-field v-model="formData.code" label="Code" variant="outlined" />
            </v-col>

            <!-- Name -->
            <v-col cols="12" md="6">
              <v-text-field v-model="formData.name" label="Name" variant="outlined" />
            </v-col>

            <!-- Symbol Token -->
            <v-col cols="12" md="6">
              <v-text-field v-model="formData.symbol_token" label="Symbol Token" variant="outlined" />
            </v-col>

            <!-- Exchange -->
            <v-col cols="12" md="6">
              <v-select v-model.number="formData.exchange_id" :items="exchangeOptions" item-title="label" item-value="value" label="Select Exchange" variant="outlined" />
            </v-col>

            <!-- Quantity -->
            <v-col cols="12" md="6">
              <v-text-field v-model.number="formData.qty" label="Quantity" type="number" variant="outlined" required />
            </v-col>

            <!-- Average Price -->
            <v-col cols="12" md="6">
              <v-text-field v-model.number="formData.avg_price" label="Average Price" type="number" variant="outlined" required />
            </v-col>

            <!-- Market Price -->
            <v-col cols="12" md="6">
              <v-text-field v-model.number="formData.market_price" label="Market Price" type="number" variant="outlined" />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" @click="submitForm">{{ isEditMode ? 'Update' : 'Add' }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { reactive, ref, computed, watch } from 'vue'
import { useAlertStore } from '@/stores'

const props = defineProps({
  modelValue: Boolean,
  editData: Object,
  platformOptions: { type: Array, default: () => [] },
  exchangeOptions: { type: Array, default: () => [] },
  stock:           { type: Array, default: () => [] },
  fetchStocks: Function,
  fetchStockData: Function
})

const emit = defineEmits(['update:modelValue', 'submit'])
const alertStore$ = useAlertStore()

// Dialog & edit mode
const dialog = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})
const isEditMode = computed(() => !!props.editData?.trade_stock_id)

// Custom stock ID
const CUSTOM_STOCK_ID = 'custom'

// Platform & stock state
const selectedPlatform = ref(null)
const platformStocks = ref([])

// Form data
const formData = reactive({
  stock_id: null,
  code: '',
  name: '',
  symbol_token: '',
  exchange_id: 0,
  qty: 0,
  avg_price: 0,
  market_price: 0
})

// Stock options with custom + platform filter
const computedStockOptions = computed(() => {
  // Always include the "Add Custom Stock" option
  const options = [{ value: CUSTOM_STOCK_ID, label: '➕ Add Custom Stock' }]

  // Filter stocks based on selected platform
  if (selectedPlatform.value && props.stock?.length > 0) {

    const filteredStocks = props.stock.filter((s) => {
      return s.platform_id == selectedPlatform.value;
    });

    // Map into { value, label } objects for <v-select>
    options.push(
      ...filteredStocks.map((s) => ({
        value: s.stock_id,
        label: s.name
      }))
    )
  }

  return options
})


// Platform change → fetch stocks
async function onPlatformChange(platformId) {
  formData.stock_id = null
  platformStocks.value = []
  if (platformId && props.fetchStocks) {
    try {
      platformStocks.value = await props.fetchStocks(platformId)
    } catch (err) {
      alertStore$.error('Failed to fetch stocks')
    }
  }
}

// Stock change → autofill
async function onStockChange(selectedId) {
  if (selectedId === CUSTOM_STOCK_ID) {
    formData.code = ''
    formData.name = ''
    formData.symbol_token = ''
    formData.exchange_id = 0
    formData.qty = 0,
    formData.avg_price = 0
    formData.market_price = 0
  } else if (props.fetchStockData) {
    try {
      const stock = await props.fetchStockData(selectedId)
      if (stock) {
        Object.assign(formData, {
          code: stock.code || '',
          name: stock.name || '',
          symbol_token: stock.symbol_token || '',
          exchange_id: stock.exchange_id || 0,
          qty: stock.qty,
          avg_price: stock.avg_price || 0,
          market_price: stock.market_price || 0
        })
      }
    } catch (err) {
      alertStore$.error('Failed to fetch stock data')
    }
  }
}

// On edit → prefill form
watch(
  () => props.editData,
  async (val) => {
    if (val) {
      selectedPlatform.value = val.platform_id || null
      if (selectedPlatform.value && platformStocks.value.length === 0) {
        await onPlatformChange(selectedPlatform.value)
      }
      Object.assign(formData, {
        stock_id: val.stock_id || 0,
        code: val.code || '',
        name: val.name || '',
        symbol_token: val.symbol_token || '',
        exchange_id: Number(val.exchange_id) || 0,
        qty: val.qty || 0,
        avg_price: val.avg_price || 0,
        market_price: val.market_price || 0
      })
    } else {
      selectedPlatform.value = null
      Object.assign(formData, {
        stock_id: 0,
        code: '',
        name: '',
        symbol_token: '',
        exchange_id: 0,
        qty: 0,
        avg_price: 0,
        market_price: 0
      })
    }
  },
  { immediate: true }
)

function close() {
  emit('update:modelValue', false)
}

function submitForm() {
  const finalData = {
  ...formData,
    stock_id: formData.stock_id === CUSTOM_STOCK_ID ? 0 : formData.stock_id,
    qty: Number(formData.qty),
    avg_price: Number(formData.avg_price),
    market_price: Number(formData.market_price)
  }

  emit('submit', finalData)
  close()
}
</script>